/**
 * KET Vocabulary Data
 * Cambridge English: Key (A2 Level) vocabulary for elementary students
 */

// Theme definition
export interface KETTheme {
  id: string;
  name: string;
  icon: string;
  wordCount: number;
  description: string;
}

// Word definition
export interface KETWord {
  word: string;
  meaning: string;
  example: string;
  phonetic?: string;
}

// Study plan
export interface WeekPlan {
  week: number;
  themeIds: string[];
  themeNames: string[];
  words: number;
  focus: string;
  tips: string;
}

// Import vocabulary data from separate files
import { personalInfoWords } from './personal-info';
import { homeFamilyWords } from './home-family';
import { schoolEducationWords } from './school';
import { foodDrinkWords } from './food';
import { leisureHobbiesWords } from './leisure';
import { bodyHealthWords, shoppingWords, travelTransportWords, weatherEnvironmentWords, timeDatesWords, workJobsWords, clothesAccessoriesWords } from './other-themes';

// All vocabulary data combined
export const ketVocabularyData: Record<string, KETWord[]> = {
  personal_info: personalInfoWords,
  home_family: homeFamilyWords,
  school_education: schoolEducationWords,
  food_drink: foodDrinkWords,
  leisure_hobbies: leisureHobbiesWords,
  body_health: bodyHealthWords,
  shopping: shoppingWords,
  travel_transport: travelTransportWords,
  weather_environment: weatherEnvironmentWords,
  time_dates: timeDatesWords,
  work_jobs: workJobsWords,
  clothes_accessories: clothesAccessoriesWords,
};

// All themes metadata
export const ketThemes: KETTheme[] = [
  { id: 'personal_info', name: '个人信息', icon: '👤', wordCount: personalInfoWords.length, description: '姓名、年龄、家庭成员、问候语、数字等' },
  { id: 'home_family', name: '家与家庭', icon: '🏠', wordCount: homeFamilyWords.length, description: '房间、家具、家庭用品、方位介词等' },
  { id: 'school_education', name: '学校与教育', icon: '📚', wordCount: schoolEducationWords.length, description: '学科、教室、学习用品、考试相关词汇' },
  { id: 'food_drink', name: '食物与饮料', icon: '🍽️', wordCount: foodDrinkWords.length, description: '三餐、水果、蔬菜、饮料、烹饪词汇' },
  { id: 'leisure_hobbies', name: '休闲与爱好', icon: '🎮', wordCount: leisureHobbiesWords.length, description: '运动、音乐、游戏、爱好、娱乐场所' },
  { id: 'body_health', name: '身体与健康', icon: '💪', wordCount: bodyHealthWords.length, description: '身体部位、健康、疾病、情绪表达' },
  { id: 'shopping', name: '购物', icon: '🛒', wordCount: shoppingWords.length, description: '商店、产品、价格、支付方式' },
  { id: 'travel_transport', name: '交通与旅行', icon: '🚗', wordCount: travelTransportWords.length, description: '交通工具、车站、旅行文件、方向词汇' },
  { id: 'weather_environment', name: '天气与环境', icon: '🌤️', wordCount: weatherEnvironmentWords.length, description: '天气类型、季节、自然词汇' },
  { id: 'time_dates', name: '时间与日期', icon: '🕐', wordCount: timeDatesWords.length, description: '时间表达、星期、月份、频率词汇' },
  { id: 'work_jobs', name: '工作', icon: '💼', wordCount: workJobsWords.length, description: '职业、工作场所、工作相关词汇' },
  { id: 'clothes_accessories', name: '衣服与配饰', icon: '👕', wordCount: clothesAccessoriesWords.length, description: '服装、鞋子、配饰、颜色、材料' },
];

// Total words
export const ketTotalWords = ketThemes.reduce((sum, t) => sum + t.wordCount, 0);

// 8-week study plan
export const ketStudyPlan: WeekPlan[] = [
  { week: 1, themeIds: ['personal_info'], themeNames: ['个人信息'], words: personalInfoWords.length, focus: '个人信息基础', tips: '每天12词，结合实际使用场景，用英语介绍自己' },
  { week: 2, themeIds: ['home_family'], themeNames: ['家与家庭'], words: homeFamilyWords.length, focus: '家庭生活词汇', tips: '每天15词，画图记忆，联系日常生活' },
  { week: 3, themeIds: ['school_education'], themeNames: ['学校与教育'], words: schoolEducationWords.length, focus: '学校场景词汇', tips: '每天20词，造句练习，模拟课堂对话' },
  { week: 4, themeIds: ['food_drink'], themeNames: ['食物与饮料'], words: foodDrinkWords.length, focus: '饮食词汇', tips: '每天18词，分类记忆，烹饪实践结合' },
  { week: 5, themeIds: ['leisure_hobbies'], themeNames: ['休闲与爱好'], words: leisureHobbiesWords.length, focus: '兴趣爱好词汇', tips: '每天16词，兴趣导向，游戏化学习' },
  { week: 6, themeIds: ['body_health', 'shopping'], themeNames: ['身体与健康', '购物'], words: bodyHealthWords.length + shoppingWords.length, focus: '身体与购物', tips: '每天30词，情境模拟，购物对话练习' },
  { week: 7, themeIds: ['travel_transport', 'weather_environment'], themeNames: ['交通与旅行', '天气与环境'], words: travelTransportWords.length + weatherEnvironmentWords.length, focus: '出行与天气', tips: '每天32词，出行实践，天气预报英文版' },
  { week: 8, themeIds: ['time_dates', 'work_jobs', 'clothes_accessories'], themeNames: ['时间与日期', '工作', '衣服与配饰'], words: timeDatesWords.length + workJobsWords.length + clothesAccessoriesWords.length, focus: '综合复习与巩固', tips: '每天46词，总复习，模拟KET考试题' },
];

// KET exam info
export const ketExamInfo = {
  name: 'Cambridge English: Key (KET)',
  level: 'A2',
  description: '剑桥英语初级考试，适合小学四年级学生',
  skills: ['阅读', '写作', '听力', '口语'],
  parts: {
    reading: { parts: 5, time: '30分钟', description: '理解标识、短信、邮件等' },
    writing: { parts: 2, time: '30分钟', description: '写邮件、写故事' },
    listening: { parts: 5, time: '25分钟', description: '听对话、广播、访谈等' },
    speaking: { parts: 2, time: '8-10分钟', description: '自我介绍、问答、图片描述' },
  },
  tips: [
    '每天学习60-90分钟',
    '掌握1500个核心词汇',
    '多练习听力和口语',
    '熟悉考试题型',
    '保持学习积极性',
  ],
};

// Daily challenge types
export const dailyChallengeTypes = [
  { day: 0, type: 'vocabulary', name: '词汇复习', icon: '📚', description: '复习本周词汇', duration: 20 },
  { day: 1, type: 'reading', name: '阅读理解', icon: '📖', description: '完成5道阅读题', duration: 15 },
  { day: 2, type: 'vocabulary', name: '词汇复习', icon: '📚', description: '复习本周词汇', duration: 20 },
  { day: 3, type: 'writing', name: '写作练习', icon: '✍️', description: '写一封邮件或故事', duration: 20 },
  { day: 4, type: 'vocabulary', name: '词汇复习', icon: '📚', description: '复习本周词汇', duration: 20 },
  { day: 5, type: 'listening', name: '听力训练', icon: '🎧', description: '完成5道听力题', duration: 15 },
  { day: 6, type: 'speaking', name: '口语练习', icon: '🎤', description: '练习自我介绍', duration: 15 },
];

// Get daily challenge
export function getDailyChallenge(): { type: string; name: string; icon: string; description: string; duration: number } {
  const day = new Date().getDay();
  return dailyChallengeTypes[day];
}

// Get words by theme
export function getWordsByTheme(themeId: string): KETWord[] {
  return ketVocabularyData[themeId] || [];
}

// Get random quiz words
export function getRandomQuizWords(themeId: string, count: number = 10): KETWord[] {
  const words = getWordsByTheme(themeId);
  const shuffled = [...words].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, words.length));
}

// Get all words
export function getAllWords(): KETWord[] {
  return Object.values(ketVocabularyData).flat();
}